import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "finalization-editor" }
const _hoisted_2 = { class: "editor-container" }
const _hoisted_3 = { class: "tiptap-editor-wrapper" }
const _hoisted_4 = { class: "wysiwyg-editor" }
const _hoisted_5 = {
  key: 0,
  class: "editor-toolbar"
}
const _hoisted_6 = { class: "toolbar-group" }
const _hoisted_7 = { class: "toolbar-group" }
const _hoisted_8 = { class: "toolbar-group" }
const _hoisted_9 = { class: "toolbar-group" }
const _hoisted_10 = { class: "toolbar-group" }
const _hoisted_11 = { class: "toolbar-group" }
const _hoisted_12 = ["disabled"]
const _hoisted_13 = ["disabled"]
const _hoisted_14 = { class: "toolbar-group" }
const _hoisted_15 = { class: "editor-footer" }
const _hoisted_16 = { class: "editor-stats" }
const _hoisted_17 = { class: "editor-actions" }
const _hoisted_18 = ["disabled"]
const _hoisted_19 = { class: "preview-content" }
const _hoisted_20 = ["innerHTML"]

import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { Editor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";
import { marked } from "marked";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import Color from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import Typography from "@tiptap/extension-typography";


export default /*@__PURE__*/_defineComponent({
  __name: 'FinalizationEditor',
  props: {
    initialContent: {},
    disabled: { type: Boolean },
    isSubmitting: { type: Boolean }
  },
  emits: ["contentChange", "submitReview"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const editor = ref<Editor | null>(null);
const isPreviewMode = ref(false);

// Initialize editor
onMounted(() => {
  editor.value = new Editor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
        // Disable the default Link extension from StarterKit since we're adding a custom one
        link: false,
      }),
      Markdown.configure({
        html: true, // Allow HTML rendering for WYSIWYG mode
        tightLists: true, // Render lists properly
        bulletListMarker: "-", // Use dashes for bullet lists
        linkify: true, // Auto-convert URLs to links
        breaks: true, // Convert line breaks to <br>
        transformPastedText: true, // Transform pasted content
        transformCopiedText: true, // Transform copied content
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "editor-link",
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: "editor-image",
        },
      }),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Highlight.configure({
        multicolor: false,
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Underline,
      Placeholder.configure({
        placeholder: "Aloita kirjoittamaan arviointia...",
      }),
      Color,
      TextStyle,
      Typography,
    ],
    content: "", // Start empty, will be set by watch
    editable: !props.disabled,
    editorProps: {
      attributes: {
        class: "prosemirror-editor",
      },
      handleKeyDown: (view, event) => {
        // Handle custom keyboard shortcuts
        if (event.ctrlKey || event.metaKey) {
          switch (event.key) {
            case "b":
              event.preventDefault();
              editor.value?.chain().focus().toggleBold().run();
              return true;
            case "i":
              event.preventDefault();
              editor.value?.chain().focus().toggleItalic().run();
              return true;
            case "u":
              event.preventDefault();
              editor.value?.chain().focus().toggleUnderline().run();
              return true;
            case "k":
              event.preventDefault();
              addLink();
              return true;
            case "Enter":
              event.preventDefault();
              editor.value?.chain().focus().setHardBreak().run();
              return true;
          }
        }

        // Handle Alt/Option shortcuts
        if (event.altKey) {
          switch (event.key) {
            case "1":
              event.preventDefault();
              editor.value?.chain().focus().toggleHeading({ level: 1 }).run();
              return true;
            case "2":
              event.preventDefault();
              editor.value?.chain().focus().toggleHeading({ level: 2 }).run();
              return true;
            case "3":
              event.preventDefault();
              editor.value?.chain().focus().toggleHeading({ level: 3 }).run();
              return true;
          }
        }

        return false;
      },
    },
    onUpdate: ({ editor }) => {
      // Get markdown from storage for content change events
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
      const content =
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
        (editor.storage.markdown as any)?.getMarkdown?.() || editor.getHTML();
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const contentStr = content as string;
      console.log(
        "📝 FinalizationEditor content updated, emitting contentChange:",
        {
          contentLength: contentStr.length,
          content:
            contentStr.substring(0, 100) +
            (contentStr.length > 100 ? "..." : ""),
        },
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      emit("contentChange", content);
    },
  });
});

// Cleanup editor
onBeforeUnmount(() => {
  if (editor.value) {
    editor.value.destroy();
  }
});

// Watch for content changes from parent
watch(
  () => props.initialContent,
  (newContent) => {
    console.log("📝 FinalizationEditor received initial content:", {
      contentLength: newContent?.length || 0,
      content:
        (newContent?.substring(0, 100) || "") +
        (newContent && newContent.length > 100 ? "..." : ""),
      editorReady: !!editor.value,
    });

    // If editor is ready, set content immediately
    if (newContent !== undefined && editor.value) {
      editor.value.commands.setContent(newContent);
      console.log("📝 FinalizationEditor content set in editor immediately");
    } else if (newContent !== undefined) {
      // If editor is not ready yet, wait for it to be ready
      console.log("📝 FinalizationEditor waiting for editor to be ready...");
      const trySetContent = () => {
        if (editor.value) {
          editor.value.commands.setContent(newContent);
          console.log("📝 FinalizationEditor content set in editor after wait");
        } else {
          // Try again in the next tick
          setTimeout(trySetContent, 10);
        }
      };
      trySetContent();
    }
  },
  { immediate: true },
);

// Watch for disabled state changes
watch(
  () => props.disabled,
  (disabled) => {
    if (editor.value) {
      editor.value.setEditable(!disabled);
    }
  },
);

// Computed properties for stats
const wordCount = computed(() => {
  const text = editor.value?.getText() || "";
  return text.trim() ? text.trim().split(/\s+/).length : 0;
});

const charCount = computed(() => {
  return editor.value?.getText().length || 0;
});

const canSubmit = computed(() => {
  return wordCount.value > 0 && !props.disabled;
});

const previewHtml = computed(() => {
  const content = getCurrentContent();
  return marked(content);
});

// Helper function to get current content
const getCurrentContent = () => {
  if (editor.value) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
    const content =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
      (editor.value.storage.markdown as any)?.getMarkdown?.() ||
      editor.value.getHTML();
    return content as string;
  }
  return "";
};

// Methods
const togglePreview = () => {
  isPreviewMode.value = !isPreviewMode.value;
};

const submitReview = () => {
  if (!canSubmit.value) return;

  // Render the content as HTML for the final submit
  const content = previewHtml.value;
  emit("submitReview", content);
};

// Toolbar helper methods
const addLink = () => {
  if (!editor.value) return;

  const url = window.prompt("Syötä linkki:");
  if (url) {
    editor.value.chain().focus().setLink({ href: url }).run();
  }
};

const addImage = () => {
  if (!editor.value) return;

  const url = window.prompt("Syötä kuvan URL:");
  if (url) {
    editor.value.chain().focus().setImage({ src: url }).run();
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[23] || (_cache[23] = _createElementVNode("div", { class: "editor-header" }, [
        _createElementVNode("h3", null, "Viimeistele arviointi"),
        _createElementVNode("p", { class: "editor-subtitle" }, " Muokkaa valittua arviointia ja viimeistele se lähetettäväksi opiskelijalle. ")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          (editor.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("button", {
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (editor.value.chain().focus().toggleBold().run())),
                    class: _normalizeClass([{ 'is-active': editor.value.isActive('bold') }, "toolbar-button"]),
                    title: "Bold (Ctrl+B)"
                  }, [...(_cache[19] || (_cache[19] = [
                    _createElementVNode("strong", null, "B", -1)
                  ]))], 2),
                  _createElementVNode("button", {
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (editor.value.chain().focus().toggleItalic().run())),
                    class: _normalizeClass([{ 'is-active': editor.value.isActive('italic') }, "toolbar-button"]),
                    title: "Italic (Ctrl+I)"
                  }, [...(_cache[20] || (_cache[20] = [
                    _createElementVNode("em", null, "I", -1)
                  ]))], 2),
                  _createElementVNode("button", {
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (editor.value.chain().focus().toggleUnderline().run())),
                    class: _normalizeClass([{ 'is-active': editor.value.isActive('underline') }, "toolbar-button"]),
                    title: "Underline (Ctrl+U)"
                  }, [...(_cache[21] || (_cache[21] = [
                    _createElementVNode("u", null, "U", -1)
                  ]))], 2),
                  _createElementVNode("button", {
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (editor.value.chain().focus().toggleStrike().run())),
                    class: _normalizeClass([{ 'is-active': editor.value.isActive('strike') }, "toolbar-button"]),
                    title: "Strikethrough"
                  }, [...(_cache[22] || (_cache[22] = [
                    _createElementVNode("s", null, "S", -1)
                  ]))], 2)
                ]),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("button", {
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (
                  editor.value.chain().focus().toggleHeading({ level: 1 }).run()
                )),
                    class: _normalizeClass([{
                  'is-active': editor.value.isActive('heading', { level: 1 }),
                }, "toolbar-button"]),
                    title: "Heading 1"
                  }, " H1 ", 2),
                  _createElementVNode("button", {
                    onClick: _cache[5] || (_cache[5] = ($event: any) => (
                  editor.value.chain().focus().toggleHeading({ level: 2 }).run()
                )),
                    class: _normalizeClass([{
                  'is-active': editor.value.isActive('heading', { level: 2 }),
                }, "toolbar-button"]),
                    title: "Heading 2"
                  }, " H2 ", 2),
                  _createElementVNode("button", {
                    onClick: _cache[6] || (_cache[6] = ($event: any) => (
                  editor.value.chain().focus().toggleHeading({ level: 3 }).run()
                )),
                    class: _normalizeClass([{
                  'is-active': editor.value.isActive('heading', { level: 3 }),
                }, "toolbar-button"]),
                    title: "Heading 3"
                  }, " H3 ", 2)
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("button", {
                    onClick: _cache[7] || (_cache[7] = ($event: any) => (editor.value.chain().focus().toggleBulletList().run())),
                    class: _normalizeClass([{ 'is-active': editor.value.isActive('bulletList') }, "toolbar-button"]),
                    title: "Bullet List"
                  }, " • ", 2),
                  _createElementVNode("button", {
                    onClick: _cache[8] || (_cache[8] = ($event: any) => (editor.value.chain().focus().toggleOrderedList().run())),
                    class: _normalizeClass([{ 'is-active': editor.value.isActive('orderedList') }, "toolbar-button"]),
                    title: "Ordered List"
                  }, " 1. ", 2),
                  _createElementVNode("button", {
                    onClick: _cache[9] || (_cache[9] = ($event: any) => (editor.value.chain().focus().toggleBlockquote().run())),
                    class: _normalizeClass([{ 'is-active': editor.value.isActive('blockquote') }, "toolbar-button"]),
                    title: "Quote"
                  }, " \" ", 2)
                ]),
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("button", {
                    onClick: _cache[10] || (_cache[10] = ($event: any) => (editor.value.chain().focus().setTextAlign('left').run())),
                    class: _normalizeClass([{ 'is-active': editor.value.isActive({ textAlign: 'left' }) }, "toolbar-button"]),
                    title: "Align Left"
                  }, " ⇤ ", 2),
                  _createElementVNode("button", {
                    onClick: _cache[11] || (_cache[11] = ($event: any) => (editor.value.chain().focus().setTextAlign('center').run())),
                    class: _normalizeClass([{
                  'is-active': editor.value.isActive({ textAlign: 'center' }),
                }, "toolbar-button"]),
                    title: "Align Center"
                  }, " ⇔ ", 2),
                  _createElementVNode("button", {
                    onClick: _cache[12] || (_cache[12] = ($event: any) => (editor.value.chain().focus().setTextAlign('right').run())),
                    class: _normalizeClass([{
                  'is-active': editor.value.isActive({ textAlign: 'right' }),
                }, "toolbar-button"]),
                    title: "Align Right"
                  }, " ⇥ ", 2)
                ]),
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("button", {
                    onClick: _cache[13] || (_cache[13] = ($event: any) => (editor.value.chain().focus().toggleHighlight().run())),
                    class: _normalizeClass([{ 'is-active': editor.value.isActive('highlight') }, "toolbar-button"]),
                    title: "Highlight"
                  }, " ⬛ ", 2),
                  _createElementVNode("button", {
                    onClick: _cache[14] || (_cache[14] = ($event: any) => (editor.value.chain().focus().toggleCode().run())),
                    class: _normalizeClass([{ 'is-active': editor.value.isActive('code') }, "toolbar-button"]),
                    title: "Code"
                  }, " <> ", 2),
                  _createElementVNode("button", {
                    onClick: _cache[15] || (_cache[15] = ($event: any) => (editor.value.chain().focus().toggleCodeBlock().run())),
                    class: _normalizeClass([{ 'is-active': editor.value.isActive('codeBlock') }, "toolbar-button"]),
                    title: "Code Block"
                  }, " {} ", 2)
                ]),
                _createElementVNode("div", _hoisted_11, [
                  _createElementVNode("button", {
                    onClick: _cache[16] || (_cache[16] = ($event: any) => (editor.value.chain().focus().undo().run())),
                    disabled: !editor.value.can().undo(),
                    class: "toolbar-button",
                    title: "Undo (Ctrl+Z)"
                  }, " ↶ ", 8, _hoisted_12),
                  _createElementVNode("button", {
                    onClick: _cache[17] || (_cache[17] = ($event: any) => (editor.value.chain().focus().redo().run())),
                    disabled: !editor.value.can().redo(),
                    class: "toolbar-button",
                    title: "Redo (Ctrl+Y)"
                  }, " ↷ ", 8, _hoisted_13)
                ]),
                _createElementVNode("div", _hoisted_14, [
                  _createElementVNode("button", {
                    onClick: addLink,
                    class: _normalizeClass([{ 'is-active': editor.value.isActive('link') }, "toolbar-button"]),
                    title: "Add Link"
                  }, " 🔗 ", 2),
                  _createElementVNode("button", {
                    onClick: addImage,
                    class: "toolbar-button",
                    title: "Add Image"
                  }, " 🖼️ ")
                ])
              ]))
            : _createCommentVNode("", true),
          _createVNode(_unref(EditorContent), {
            editor: editor.value,
            class: "tiptap-editor"
          }, null, 8, ["editor"])
        ])
      ]),
      _createElementVNode("div", _hoisted_15, [
        _createElementVNode("div", _hoisted_16, [
          _createElementVNode("span", null, _toDisplayString(wordCount.value) + " sanaa · " + _toDisplayString(charCount.value) + " merkkiä", 1)
        ]),
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("button", {
            class: _normalizeClass(["preview-button", { active: isPreviewMode.value }]),
            onClick: togglePreview
          }, _toDisplayString(isPreviewMode.value ? "Muokkaa" : "Esikatselu"), 3),
          _createElementVNode("button", {
            class: "submit-button",
            onClick: submitReview,
            disabled: !canSubmit.value || props.isSubmitting
          }, _toDisplayString(props.isSubmitting
                ? "Lähetetään..."
                : "Lähetä arviointi opiskelijalle"), 9, _hoisted_18)
        ])
      ])
    ]),
    (isPreviewMode.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "preview-modal-overlay",
          onClick: togglePreview
        }, [
          _createElementVNode("div", {
            class: "preview-modal",
            onClick: _cache[18] || (_cache[18] = _withModifiers(() => {}, ["stop"]))
          }, [
            _createElementVNode("div", { class: "preview-header" }, [
              _cache[24] || (_cache[24] = _createElementVNode("span", { class: "preview-title" }, "Esikatselu", -1)),
              _createElementVNode("button", {
                class: "close-button",
                onClick: togglePreview
              }, "×")
            ]),
            _createElementVNode("div", _hoisted_19, [
              _createElementVNode("div", {
                class: "document-body",
                innerHTML: previewHtml.value
              }, null, 8, _hoisted_20)
            ]),
            _createElementVNode("div", { class: "preview-footer" }, [
              _createElementVNode("button", {
                class: "close-preview-button",
                onClick: togglePreview
              }, " Sulje esikatselu ")
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})